<script lang="ts">
const DECORATION_CLASS = "text-chunk-decoration"
</script>
<script setup lang="ts">
import * as monaco from "monaco-editor"

const props = defineProps({
  file: null,
  peek: { type: Boolean },
  showMatchedChunks: { type: Boolean }
})

const EditorOption = monaco.editor.EditorOption


const { file } = toRefs(props)
const { shouldNavElementsBeHidden } = usePreview()
const { rendererState } = useRenderer({ file })
const { createMonacoRenderer, languages } = useMonacoEditor()
const { previewNoFade } = useGlobals()
const renderer = useTemplateRef("renderer")
let editorInstance: Promise<monaco.editor.IStandaloneCodeEditor> | undefined
const language = ref("Loading...")
const wordWrap = ref("on") as Ref<"off" | "on">
const highlighting = ref("on") as Ref<"off" | "on">
const fontSize = ref(12)

async function getEditor() {
  const editor = await editorInstance
  if (!isDefined(editor))
    throw new Error("Editor instance not defined")
  return editor
}

const decorations = computed<monaco.editor.IModelDeltaDecoration[] | undefined>(() => {
  if (!props.showMatchedChunks)
    return

  const color = "#0496ff80" // secondary blue
  
  // a decoration is start line, start column, end line, end column (all 1-based)
  return props.file.matchedChunks()
    ?.filter(chunk => chunk.chunkType === "polyEmbedV1Page")
    .filter(chunk => isDefined(chunk.startLine)
      && isDefined(chunk.startChar)
      && isDefined(chunk.endLine)
      && isDefined(chunk.endChar),
    )
    .map(chunk => ({
      range: new monaco.Range(
        chunk.startLine!,
        chunk.startChar!,
        chunk.endLine!,
        chunk.endChar!,
      ),
      options: {
        inlineClassName: DECORATION_CLASS,
        glyphMarginClassName: `${DECORATION_CLASS}-glyph`,
        glyphMarginHoverMessage: { value: "Search Result" },
        // Minimap decoration
        minimap: {
          color,
          position: monaco.editor.MinimapPosition.Inline,
        },
        // Overview ruler (scrollbar) decoration
        overviewRuler: {
          color,
          position: monaco.editor.OverviewRulerLane.Center,
        },
      },
    }))
})
const currentDecorations = ref<string[]>()

function refreshDecorations(editor: monaco.editor.IStandaloneCodeEditor) {
  currentDecorations.value = editor.deltaDecorations(
    currentDecorations.value ?? [],
    decorations.value ?? [],
  )
}

async function setTextContent(objectUrl?: string) {
  if (!objectUrl)
    return
  const response = await fetch(objectUrl)
  const text = await response.text()
  const editor = await getEditor()
  editor.setValue(text)
  refreshDecorations(editor)
}

// This may change from cached to server, so we need to watch for updates.
watch(() => rendererState.value.objectUrl, setTextContent)
onMounted(async () => {
  if (!isDefined(renderer.value))
    return console.error("The renderer is not defined")
  editorInstance = createMonacoRenderer(renderer.value, props.peek, {
    wordWrap: wordWrap.value,
    fontSize: fontSize.value,
  })
  const editor = await editorInstance
  wordWrap.value = editor.getOption(EditorOption.wordWrap) as any
  fontSize.value = editor.getOption(EditorOption.fontSize)
  language.value = editor.getModel()?.getLanguageId() ?? "Unknown"
})

onUnmounted(async () => {
  const editor = await getEditor()
  editor.dispose()
})

watch(fontSize, async fontSize => (await editorInstance)?.updateOptions({ fontSize }))
watch(wordWrap, async wordWrap => (await editorInstance)?.updateOptions({ wordWrap }))
watch(language, async (language, oldLanguage) => {
  const editor = await editorInstance!
  const model = editor.getModel()
  if (!isDefined(model))
    return console.error("Unable to set monaco language")
  if (!language || !oldLanguage)
    return
  monaco.editor.setModelLanguage(model, language)

  refreshDecorations(editor)
})
watch(highlighting, async (highlighting) => {
  const editor = await editorInstance!
  const model = editor.getModel()
  if (!isDefined(model))
    return console.error("Unable to set monaco highlighting")
  if (highlighting === "off")
    monaco.editor.setModelLanguage(model, "plaintext")
  else if (highlighting === "on")
    monaco.editor.setModelLanguage(model, language.value!)
  else
    console.error("Unknown highlighting option", highlighting)

  refreshDecorations(editor)
})
</script>

<template>
  <div class="renderer-container" :class="{ peek }">
    <div ref="renderer" class="renderer-monaco" />
    <div class="toolbar-container">
      <div
        class="toolbar"
        :class="{
          [previewNoFade]: true,
          'fade-out': shouldNavElementsBeHidden,
        }"
      >
        <div class="toolbar-frame">
          <div class="toolbar-syntax">
            <div>Highlighting</div>
            <div
              class="toolbar-syntax-button"
              :class="[highlighting]"
              @click="highlighting = highlighting === 'on' ? 'off' : 'on'"
            >
              {{ highlighting }}
            </div>
          </div>
          <div class="toolbar-language">
            <div>Language</div>
            <menu-selection
              v-model="language"
              class="toolbar-language-select"
              :options="Object.keys(languages)"
              :label="x => (languages as any)[x]"
              :disabled="highlighting === 'off'"
              direction="up"
            />
          </div>
          <div class="toolbar-wrap">
            <div>Word Wrap</div>
            <div
              class="toolbar-wrap-button"
              :class="[wordWrap]"
              @click="wordWrap = wordWrap === 'on' ? 'off' : 'on'"
            >
              {{ wordWrap }}
            </div>
          </div>
          <div class="toolbar-size">
            <div class="plus" @click="fontSize = fontSize + 1">
              <icon-action size="xs" type="plus" />
            </div>
            <div class="minus" @click="fontSize = fontSize - 1">
              <icon-action size="xs" type="minus" />
            </div>
            <menu-selection
              v-model="fontSize"
              class="toolbar-font-size-select"
              :options="[6, 7, 8, 9, 10, 11, 12, 13, 14, 16, 18, 24, 28, 32, 36, 40, 48, 64]"
              :label="x => x.toString()"
              direction="up"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.renderer-container
  @apply flex items-center justify-center
  &.peek
    @apply w-[75vw] h-[75vh]
  &:not(.peek)
    @apply w-full h-full

.renderer-monaco
  @apply w-full h-full
  @apply bg-[#212123D0]
  // override the vscode.focusBorder css variable

.toolbar-container
  @apply absolute bottom-[#{$nav-gap-pills}] left-0 right-0 flex justify-center

.toolbar
  @apply pointer-events-auto z-overlay-layer relative
  @apply mx-auto
  @apply inline-flex h-[#{$nav-toolbar-h-preview-toolbar}] rounded-full
  @apply bg-[#{$nav-toolbar-bg-default}] shadow-menu
  @apply t-menu-xs text-[#{$global-t-default}]
  opacity: 1
  transition: opacity 0.35s ease-in-out
  &.fade-out
    opacity: 0.0
  &::before
    @apply overlay-blur rounded-full

.toolbar-frame
  @apply w-full h-full rounded-full
  @apply px-[#{$nav-toolbar-px-default}]
  @apply flex justify-center items-center gap-[#{$nav-toolbar-gap-default}]
  &::before
    @apply parent-menu-dropdown-blur rounded-full

.toolbar-language, .toolbar-wrap, .toolbar-size, .toolbar-syntax
  @apply flex gap-[#{$nav-toolbar-gap-section}]

.toolbar-language-select, .toolbar-font-size-select
  @apply ml-1 capitalize

.toolbar-syntax
  .toolbar-syntax-button
    @apply t-menu-xs capitalize
    @apply px-[3px] rounded-sm bg-[#{$nav-toolbar-bg-section-fragment-select}]
    @apply cursor-pointer
    &:not(.on)
      @apply text-[#{$menu-t-item-disabled}]

.toolbar-wrap
  .toolbar-wrap-button
    @apply t-menu-xs capitalize
    @apply px-[3px] rounded-sm bg-[#{$nav-toolbar-bg-section-fragment-select}]
    @apply cursor-pointer
    &:not(.on)
      @apply text-[#{$menu-t-item-disabled}]

.toolbar-size
  .plus, .minus
    @apply rounded-sm
    &:hover
      @apply bg-[#{$nav-toolbar-bg-section-fragment-select}] cursor-pointer

</style>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

// we don't apply anything to the text highlight itself, but we could if we
// wanted here
.text-chunk-decoration
  @apply 

// The gutter style. We style it as a vertical bar that's rounded at the top
// and the bottom
.text-chunk-decoration-glyph
  @apply bg-$global-blue-primary
  @apply w-[6px]!
  &:first-of-type
    border-radius: 3px 3px 0px 0px
  &:last-of-type
    border-radius: 0px 0px 3px 3px
</style>